import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import { useDebounce } from "@/hooks/useDebounce";
import { useTahun } from "@/hooks/useTahun";
import { formatDate } from "@/lib/utils";
import { getListJalurJenis, getListUnitkerja } from "@/services/getData";
import {
  DataPendaftaran,
  JalurJenis,
  PaginationData,
  Unitkerja,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import { LuListFilter } from "react-icons/lu";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FormFilter from "./FormFilter";
import { Pagination } from "./Pagination";

interface DataTableProps<TData extends DataPendaftaran, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataPendaftaran[]>;
  isPending: boolean;
}

export function DataTable<TData extends DataPendaftaran, TValue>({
  columns,
  data,
  paginationData,
  isPending: isPendingDataPendaftaran,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { apiBaseUrl } = useApiBaseUrl();
  const [searchParams] = useSearchParams();
  const ids_ref_jalur_jenis = searchParams.get("ids_ref_jalur_jenis");
  const ids_uk = searchParams.get("ids_uk");

  const { selectedTahun } = useTahun();

  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
    },
  });

  const { data: listJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  });
  const ids_ref_jalur_jenis_array = ids_ref_jalur_jenis?.split("-");
  const listJalurJenisAktifFilter =
    listJalurJenis?.filter((item: JalurJenis) => {
      return ids_ref_jalur_jenis_array?.includes(
        String(item.id_ref_jalur_jenis),
      );
    }) || [];

  const { data: listUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  });
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active;
  });
  const ids_uk_array = ids_uk?.split("-");
  const listProdiAktifFilter =
    listProdi?.filter((item: Unitkerja) => {
      return ids_uk_array?.includes(String(item.uk_id));
    }) || [];

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      // if (params.get("search") !== debouncedQuery) {
      //   params.delete("page");
      // }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  // const renderExpandedContent = (dataPendaftaran: DataPendaftaran) => {
  //   // Helper variables
  //   const dataSiswa = dataPendaftaran?.data_siswa;
  //   const pathFoto = dataSiswa?.path_foto;
  //   const dataBerkas = dataPendaftaran?.data_berkas || [];

  //   return (
  //     <div className="p-4 bg-muted/50 space-y-4">
  //       {/* === BAGIAN ATAS: INFORMASI UTAMA (GRID 5 KOLOM) === */}
  //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
  //         {/* --- KOLOM 1: KARTU FOTO --- */}
  //         <Card className="lg:col-span-1 border-primary/20 shadow-sm h-full">
  //           <CardHeader className="pb-2">
  //             <CardTitle className="text-sm font-medium text-center">
  //               Pas Foto
  //             </CardTitle>
  //           </CardHeader>
  //           <CardContent className="flex flex-col items-center justify-center pt-2 pb-6">
  //             <div className="relative w-full max-w-[180px] aspect-[3/4] rounded-lg overflow-hidden border-2 border-muted bg-slate-100 shadow-sm group">
  //               {pathFoto ? (
  //                 <a href={pathFoto} target="_blank" rel="noreferrer">
  //                   <img
  //                     src={pathFoto}
  //                     alt="Foto Siswa"
  //                     className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
  //                   />
  //                 </a>
  //               ) : (
  //                 <div className="flex h-full w-full flex-col items-center justify-center text-muted-foreground">
  //                   <span className="text-xs">Tidak ada foto</span>
  //                 </div>
  //               )}
  //             </div>
  //             <div className="mt-4 text-center">
  //               <Badge variant="outline" className="text-[10px]">
  //                 ID: {dataSiswa?.id_data_siswa ?? "-"}
  //               </Badge>
  //             </div>
  //           </CardContent>
  //         </Card>

  //         {/* --- KOLOM 2: BIODATA --- */}
  //         <Card className="lg:col-span-1 h-full">
  //           <CardHeader className="pb-2">
  //             <CardTitle className="text-sm font-medium">Biodata</CardTitle>
  //           </CardHeader>
  //           <CardContent className="space-y-3">
  //             <div>
  //               <span className="text-xs font-medium text-muted-foreground">
  //                 Nama Lengkap
  //               </span>
  //               <p className="text-sm font-semibold text-foreground leading-tight">
  //                 {dataPendaftaran?.data_siswa?.nama_lengkap}
  //               </p>
  //             </div>
  //             <div>
  //               <span className="text-xs font-medium text-muted-foreground">
  //                 Email
  //               </span>
  //               <p className="text-sm text-foreground break-all">
  //                 {dataPendaftaran?.data_siswa?.email}
  //               </p>
  //             </div>
  //             <div>
  //               <span className="text-xs font-medium text-muted-foreground">
  //                 No. Handphone
  //               </span>
  //               <p className="text-sm text-foreground">
  //                 {dataPendaftaran?.data_siswa?.handphone}
  //               </p>
  //             </div>
  //           </CardContent>
  //         </Card>

  //         {/* --- KOLOM 3: DATA PENDAFTARAN --- */}
  //         <Card className="lg:col-span-1 h-full">
  //           <CardHeader className="pb-2">
  //             <CardTitle className="text-sm font-medium">
  //               Data Pendaftaran
  //             </CardTitle>
  //           </CardHeader>
  //           <CardContent className="space-y-2">
  //             <div className="grid grid-cols-2 gap-1">
  //               <div>
  //                 <span className="text-xs font-medium text-muted-foreground">
  //                   Tahun
  //                 </span>
  //                 <p className="text-sm">{dataPendaftaran?.tahun}</p>
  //               </div>
  //               <div>
  //                 <span className="text-xs font-medium text-muted-foreground">
  //                   KIPK
  //                 </span>
  //                 <p className="text-sm">{dataPendaftaran?.no_kipk ?? "-"}</p>
  //               </div>
  //             </div>

  //             <div>
  //               <span className="text-xs font-medium text-muted-foreground">
  //                 Jalur
  //               </span>
  //               <p
  //                 className="text-sm truncate"
  //                 title={dataPendaftaran?.periode?.nama_jalur_jenis}
  //               >
  //                 {dataPendaftaran?.periode?.nama_jalur_jenis}
  //               </p>
  //             </div>

  //             <div>
  //               <span className="text-xs font-medium text-muted-foreground">
  //                 No. Pendaftaran
  //               </span>
  //               <p className="text-sm font-mono bg-slate-100 px-1 py-0.5 rounded w-fit">
  //                 {dataPendaftaran?.nomor_pendaftaran}
  //               </p>
  //             </div>

  //             <div className="pt-1">
  //               <div className="flex justify-between items-center mb-1">
  //                 <span className="text-xs text-muted-foreground">
  //                   Validasi:
  //                 </span>
  //                 {!dataPendaftaran.is_valid_pendaftaran ? (
  //                   <Badge variant="secondary" className="text-[10px] px-1 h-5">
  //                     Belum
  //                   </Badge>
  //                 ) : (
  //                   <Badge className="text-[10px] px-1 h-5">Valid</Badge>
  //                 )}
  //               </div>
  //               <div className="flex justify-between items-center">
  //                 <span className="text-xs text-muted-foreground">
  //                   Kelulusan:
  //                 </span>
  //                 {!dataPendaftaran.is_lulus ? (
  //                   <Badge variant="secondary" className="text-[10px] px-1 h-5">
  //                     Belum
  //                   </Badge>
  //                 ) : (
  //                   <Badge variant="success" className="text-[10px] px-1 h-5">
  //                     Lulus
  //                   </Badge>
  //                 )}
  //               </div>
  //             </div>
  //           </CardContent>
  //         </Card>

  //         {/* --- KOLOM 4: PILIHAN PRODI --- */}
  //         <Card className="lg:col-span-1 h-full">
  //           <CardHeader className="pb-2">
  //             <CardTitle className="text-sm font-medium">
  //               Pilihan Prodi
  //             </CardTitle>
  //           </CardHeader>
  //           <CardContent className="space-y-2">
  //             {[...Array(4)].map((_, index) => {
  //               const pilihanProdi = dataPendaftaran.data_pilihan_prodi?.find(
  //                 (prodi) => prodi.urutan_program_studi === index + 1,
  //               );

  //               return (
  //                 <div
  //                   key={index}
  //                   className="border-b border-border/50 pb-1 last:border-0 last:pb-0"
  //                 >
  //                   <div className="flex items-start gap-2">
  //                     <span className="text-[10px] font-bold text-muted-foreground min-w-[15px] mt-0.5">
  //                       {index + 1}.
  //                     </span>
  //                     {pilihanProdi ? (
  //                       <div className="flex flex-col">
  //                         <span
  //                           className={`text-xs font-medium ${pilihanProdi.is_lulus ? "text-primary font-bold" : ""}`}
  //                         >
  //                           {pilihanProdi?.unitkerja?.nama}
  //                         </span>
  //                         {pilihanProdi?.is_lulus && (
  //                           <Badge
  //                             variant="success"
  //                             className="w-fit text-[9px] h-4 px-1 mt-0.5"
  //                           >
  //                             Diterima Disini
  //                           </Badge>
  //                         )}
  //                       </div>
  //                     ) : (
  //                       <span className="text-xs text-muted-foreground italic">
  //                         -
  //                       </span>
  //                     )}
  //                   </div>
  //                 </div>
  //               );
  //             })}
  //           </CardContent>
  //         </Card>

  //         {/* --- KOLOM 5: UKT & TAGIHAN --- */}
  //         <Card className="lg:col-span-1 h-full">
  //           <CardHeader className="pb-2">
  //             <CardTitle className="text-sm font-medium">
  //               Keuangan & Status
  //             </CardTitle>
  //           </CardHeader>
  //           <CardContent className="space-y-3">
  //             {/* Tagihan Box */}
  //             <div className="bg-slate-50 border rounded-md p-2">
  //               <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
  //                 Tagihan Pendaftaran
  //               </span>
  //               {dataPendaftaran?.data_tagihan_pendaftaran?.length ? (
  //                 dataPendaftaran.data_tagihan_pendaftaran.map((tagihan) => (
  //                   <div
  //                     key={tagihan.id_data_tagihan_daftar_plaza}
  //                     className="flex justify-between items-center text-xs mb-1 last:mb-0"
  //                   >
  //                     <span>
  //                       {tagihan.amount
  //                         ? formatRupiah(Number(tagihan.amount))
  //                         : "Rp 0"}
  //                     </span>
  //                     {tagihan.status_bayar == 2 ? (
  //                       <Badge
  //                         variant="success"
  //                         className="text-[9px] h-4 px-1"
  //                       >
  //                         Lunas
  //                       </Badge>
  //                     ) : (
  //                       <Badge
  //                         variant="secondary"
  //                         className="text-[9px] h-4 px-1"
  //                       >
  //                         Belum
  //                       </Badge>
  //                     )}
  //                   </div>
  //                 ))
  //               ) : (
  //                 <span className="text-xs text-muted-foreground italic">
  //                   Tidak ada tagihan
  //                 </span>
  //               )}
  //             </div>

  //             <div className="space-y-1">
  //               <div className="flex justify-between items-center">
  //                 <span className="text-xs text-muted-foreground">
  //                   Validasi UKT
  //                 </span>
  //                 {!dataPendaftaran.is_valid_isi_ukt ? (
  //                   <Badge variant="secondary" className="text-[9px] h-4">
  //                     Belum
  //                   </Badge>
  //                 ) : (
  //                   <Badge className="text-[9px] h-4">Valid</Badge>
  //                 )}
  //               </div>
  //               <div className="flex justify-between items-center">
  //                 <span className="text-xs text-muted-foreground">
  //                   Daftar Kembali
  //                 </span>
  //                 {!dataPendaftaran.is_daftar_kembali ? (
  //                   <Badge variant="secondary" className="text-[9px] h-4">
  //                     Belum
  //                   </Badge>
  //                 ) : (
  //                   <Badge className="text-[9px] h-4">Sudah</Badge>
  //                 )}
  //               </div>
  //             </div>

  //             <div className="pt-2 border-t">
  //               <span className="text-xs font-medium text-muted-foreground">
  //                 NIM Mahasiswa
  //               </span>
  //               <p className="text-lg font-bold text-primary tracking-tight">
  //                 {dataPendaftaran?.nim || "-"}
  //               </p>
  //             </div>
  //           </CardContent>
  //         </Card>
  //       </div>

  //       {/* === BAGIAN BAWAH: DATA BERKAS (FULL WIDTH) === */}
  //       <Card>
  //         <CardHeader className="pb-2">
  //           <CardTitle className="text-sm font-medium flex items-center gap-2">
  //             <FileText className="w-4 h-4" />
  //             Dokumen Berkas Mahasiswa
  //             <Badge variant="secondary" className="ml-2">
  //               {dataBerkas.length} File
  //             </Badge>
  //           </CardTitle>
  //         </CardHeader>
  //         <CardContent>
  //           {dataBerkas.length > 0 ? (
  //             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
  //               {dataBerkas.map((berkas, idx) => (
  //                 <a
  //                   key={idx}
  //                   href={berkas.path_berkas}
  //                   target="_blank"
  //                   rel="noopener noreferrer"
  //                   className="group flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-slate-50 hover:border-primary/50 transition-all duration-200"
  //                 >
  //                   <div className="mt-1 bg-primary/10 p-2 rounded-md group-hover:bg-primary/20 transition-colors">
  //                     <FileText className="w-5 h-5 text-primary" />
  //                   </div>
  //                   <div className="flex-1 min-w-0">
  //                     <p className="text-xs font-semibold text-foreground line-clamp-2 leading-snug group-hover:text-primary transition-colors">
  //                       {berkas.sys_berkas?.jenis_berkas?.nama_jenis_berkas ||
  //                         "Dokumen Tanpa Nama"}
  //                     </p>
  //                     <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
  //                       <span>Lihat Dokumen</span>
  //                       <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
  //                     </div>
  //                   </div>
  //                 </a>
  //               ))}
  //             </div>
  //           ) : (
  //             <div className="flex flex-col items-center justify-center py-8 text-muted-foreground border-2 border-dashed rounded-lg bg-slate-50">
  //               <FileText className="w-8 h-8 mb-2 opacity-50" />
  //               <p className="text-sm">Tidak ada berkas yang diunggah</p>
  //             </div>
  //           )}
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // };

  const DataField = ({
    label,
    value,
    className = "",
  }: {
    label: string;
    value: string | number | null | undefined;
    className?: string;
  }) => (
    <div className={`mb-2 ${className}`}>
      <span className="text-xs font-medium text-muted-foreground block">
        {label}
      </span>
      <span className="text-sm font-medium text-foreground break-words">
        {value || "-"}
      </span>
    </div>
  );

  const renderExpandedContent = (dataPendaftaran: DataPendaftaran) => {
    const ds = dataPendaftaran?.data_siswa;

    // Logic mapping sesuai PHP untuk Asal PT/Prodi
    const asalPtValue =
      dataPendaftaran?.asal_pt === "4b4b23c1-8e0c-4825-89fa-765401c5e9c5"
        ? dataPendaftaran?.asal_pt_na
        : dataPendaftaran?.ref_asal_pt?.nama_perguruan_tinggi;

    const asalProdiValue = dataPendaftaran?.asal_prodi_na
      ? dataPendaftaran?.asal_prodi_na
      : dataPendaftaran?.ref_asal_prodi?.nama_program_studi;

    return (
      <div className="p-4 bg-muted/50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. DATA PENDAFTARAN & PROGRAM */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 bg-slate-50/50 dark:bg-slate-900/50">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              Program & Pendaftaran
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <DataField
              label="Nama Program"
              value={dataPendaftaran?.ref_daftar_program?.nama_program}
            />
            <DataField
              label="No. Pendaftaran"
              value={dataPendaftaran?.nomor_pendaftaran}
              // Ubah background slate-100 menjadi slate-800 saat dark mode
              className="font-mono bg-slate-100 dark:bg-slate-800 p-1 rounded w-fit"
            />
            <DataField
              label="Tanggal Daftar"
              value={
                dataPendaftaran?.created_at
                  ? formatDate(dataPendaftaran.created_at, "medium", "en")
                  : "-"
              }
            />
            <div className="grid grid-cols-2 gap-2">
              <DataField
                label="Tahun Ajaran"
                value={dataPendaftaran?.tahun_ajaran}
              />
              <DataField label="Semester" value={dataPendaftaran?.semester} />
            </div>
          </CardContent>
        </Card>

        {/* 2. DATA DIRI PERSONAL */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 bg-slate-50/50 dark:bg-slate-900/50">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              Identitas Personal
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 space-y-3">
            <div>
              <DataField label="Nama Lengkap" value={ds?.nama_lengkap} />
            </div>

            <div className="grid grid-cols-2 gap-2 border-t pt-2 dark:border-border">
              <DataField label="No. Identitas (NIK)" value={ds?.nik} />
              <DataField label="NISN" value={ds?.nisn} />
            </div>

            <div className="grid grid-cols-2 gap-2 border-t pt-2 dark:border-border">
              <DataField
                label="Tempat, Tgl Lahir"
                value={`${ds?.tempat_lahir || ""}, ${ds?.tanggal_lahir || ""}`}
              />
              <DataField
                label="Jenis Kelamin"
                value={
                  ds?.jenis_kelamin === "L" ? "Laki-laki (1)" : "Perempuan (0)"
                }
              />
            </div>

            <div className="grid grid-cols-3 gap-2 border-t pt-2 dark:border-border">
              <DataField label="Tinggi" value={`${ds?.tinggi_badan || 0} cm`} />
              <DataField label="Berat" value={`${ds?.berat_badan || 0} kg`} />
              <DataField label="Gol. Darah" value={ds?.golongan_darah} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <DataField
                label="Alergi Obat"
                value={ds?.is_alergi_obat ? "Ya" : "Tidak"}
              />
              <DataField label="Nomor Visa" value={ds?.nomor_viza} />
            </div>
            <DataField label="Kenegaraan" value={ds?.negara?.nama_negara} />
          </CardContent>
        </Card>

        {/* 3. KONTAK & ALAMAT ASAL */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 bg-slate-50/50 dark:bg-slate-900/50">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              Alamat & Kontak
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="mb-3">
              <DataField label="Email" value={ds?.email} />
              <div className="grid grid-cols-2 gap-2">
                <DataField label="No. HP" value={ds?.handphone} />
                <DataField label="No. Telepon" value={ds?.handphone} />
              </div>
            </div>

            <div className="border-t pt-2 space-y-1 dark:border-border">
              <DataField label="Alamat Asal (Jalan)" value={ds?.jalan} />
              <DataField label="Kota Asal (Kelurahan)" value={ds?.kelurahan} />
              <DataField label="Kode Pos" value={ds?.kode_pos} />
              <DataField label="Provinsi Asal" value={ds?.provinsi_state} />
              <DataField
                label="Negara Asal (Kode)"
                value={ds?.negara?.kode_negara_feeder}
              />
            </div>
          </CardContent>
        </Card>

        {/* 4. DATA INSTITUSI & EMERGENCY */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 bg-slate-50/50 dark:bg-slate-900/50">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              Institusi & Darurat
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 space-y-4">
            {/* Asal Institusi */}
            <div>
              <Badge variant="outline" className="mb-2 text-xs">
                Asal Institusi
              </Badge>
              <DataField label="Asal Perguruan Tinggi" value={asalPtValue} />
              <DataField label="Asal Program Studi" value={asalProdiValue} />
            </div>

            {/* Kontak Darurat */}
            <div className="border-t pt-3 dark:border-border">
              <Badge
                variant="outline"
                // Perbaikan warna Badge Merah untuk Dark Mode agar tidak terlalu mencolok/silau
                className="mb-2 text-xs text-red-500 border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
              >
                Kontak Darurat
              </Badge>
              <DataField label="Nama Kontak" value={ds?.kontak_darurat_nama} />
              <DataField label="Nomor HP" value={ds?.kontak_darurat_hp} />
              <DataField
                label="Hubungan Relasi"
                value={ds?.kontak_darurat_hubungan}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by tahun, nomor pendaftaran, nama lengkap, status kelulusan, dan pilihan prodi..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
          <Button
            variant={ids_ref_jalur_jenis ? "default" : "outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowFilter(true)}
          >
            Filter
            <LuListFilter />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => {
              const url = new URL(`${apiBaseUrl}/data-pendaftaran/export`);

              const params = new URLSearchParams(window.location.search);

              if (selectedTahun) params.set("tahun", selectedTahun?.label);

              url.search = params.toString();

              window.open(url.toString(), "_blank");
            }}
          >
            Export
            <RiFileExcel2Fill />
          </Button>

          <div className="flex gap-1 flex-wrap">
            {listJalurJenisAktifFilter?.map((item: JalurJenis) => (
              <Badge variant={"outline"}>{item?.nama_jalur_jenis}</Badge>
            ))}
            {listProdiAktifFilter?.map((item: Unitkerja) => (
              <Badge variant={"outline"}>{item?.nama}</Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isPendingDataPendaftaran ? (
        <SkeletonTable />
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <>
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                          {/* {index === 0 && <TableHead></TableHead>} */}
                        </>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <>
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        </>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow>
                        <TableCell colSpan={columns.length + 5} className="p-0">
                          {renderExpandedContent(
                            row.original as DataPendaftaran,
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  );
}
